import { Spinner } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';

import {
  CmBox,
  CmButton,
  CmHeading,
  CmModalBody,
  CmModalFooter,
  CmPagination,
  CmRadio,
  CmRadioGroup,
  CmStack,
  CmText,
} from '@/components/uiParts';
import { deliveryServiceIdMap } from '@/domain/models/DeliveryService';
import { LIMIT_PER_PAGE } from '@/domain/models/Pagination';
import {
  StoreManagementMasterStoreSearchList,
  useStoreManagementMasterStoreSearchList,
} from '@/services/StoreManagement/MasterStores/hooks/serachList/useStoreManagementMasterStoreSearchList';

type Props = {
  selectedMasterStoreId?: number;
  onChangeMasterStore: (masterStore: { id: number; name: string }) => void;
  onClose: () => void;
  isPreviewButtonDisabled?: boolean;
  isPreviewButtonLoading?: boolean;
  onClickPreview: () => void;
};

type MasterStore = StoreManagementMasterStoreSearchList;

export const MasterStoreSelect: React.FC<Props> = (props) => {
  const [masterStores, setMasterStores] = useState<MasterStore[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalMasterStoreCount, setTotalMasterStoreCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const lastPage = Math.ceil(totalMasterStoreCount / LIMIT_PER_PAGE);
  const toFirstPage = () => setCurrentPage(1);
  const toLastPage = () => setCurrentPage(lastPage);
  const toNextPage = () =>
    setCurrentPage((page) => Math.min(page + 1, lastPage));
  const toPreviousPage = () => setCurrentPage((page) => Math.max(page - 1, 1));

  useEffect(() => {
    const getMasterStores = async () => {
      try {
        setIsLoading(true);
        const response = await useStoreManagementMasterStoreSearchList({
          limit: LIMIT_PER_PAGE,
          currentPage,
          name: '',
          authStoreIds: [],
          associateBrandIds: [],
          deliveryServiceIds: [deliveryServiceIdMap['Uber Eats']],
        });
        setMasterStores(response.master_stores);
        setTotalMasterStoreCount(response.count);
      } finally {
        setIsLoading(false);
      }
    };

    getMasterStores();
  }, [currentPage, setTotalMasterStoreCount, setMasterStores, setIsLoading]);

  if (!isLoading && masterStores.length === 0) {
    return (
      <>
        <CmModalBody display="flex" flexDirection="column">
          <CmText>連携済みのuber eats店舗がありません。</CmText>
          <CmText>店舗管理ページで設定してください。</CmText>
        </CmModalBody>
        <CmModalFooter>
          <CmButton onClick={props.onClose}>閉じる</CmButton>
        </CmModalFooter>
      </>
    );
  }

  return (
    <>
      <CmModalBody display="flex" flexDirection="column" minHeight={500}>
        <CmHeading size="sm" marginBottom="4">
          取り込み対象の店舗を選択してください
        </CmHeading>
        <CmBox display="flex" flexDirection="column" flexGrow={1}>
          {isLoading ? (
            <CmBox
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexGrow={1}
            >
              <CmBox>
                <Spinner />
              </CmBox>
            </CmBox>
          ) : (
            <CmRadioGroup
              value={String(props.selectedMasterStoreId)}
              onChange={(e) => {
                const masterStore = masterStores.find(
                  (store) => String(store.id) === String(e)
                );
                if (masterStore) {
                  props.onChangeMasterStore({
                    id: masterStore.id,
                    name: masterStore.name,
                  });
                }
              }}
            >
              {masterStores.map((e) => (
                <CmStack key={e.id} padding="1">
                  <CmRadio size="md" value={String(e.id)}>
                    {e.name}
                  </CmRadio>
                </CmStack>
              ))}
            </CmRadioGroup>
          )}
        </CmBox>
        {totalMasterStoreCount > masterStores.length && (
          <CmPagination
            totalNum={totalMasterStoreCount}
            currentPage={currentPage}
            toFirstPage={toFirstPage}
            toLastPage={toLastPage}
            toNextPage={toNextPage}
            toPreviousPage={toPreviousPage}
          />
        )}
      </CmModalBody>
      <CmModalFooter>
        <CmButton
          disabled={props.isPreviewButtonDisabled}
          isLoading={props.isPreviewButtonLoading}
          onClick={props.onClickPreview}
        >
          プレビュー
        </CmButton>
      </CmModalFooter>
    </>
  );
};
